import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
} from "@theme/utilities";

/** Menu button events that signal the drawer is about to open, so its content is built before the click lands */
const INTENT_EVENTS = ["pointerenter", "pointerdown", "focusin"];

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details", "menuDrawer"];
  #onChatCloseBound;
  #contentActivated = false;

  constructor() {
    super();
    this.#onChatCloseBound = this.#onChatClose.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.refs.details.addEventListener("toggle", this.#handleToggle);
    this.#setupAnimatedElementListeners();
    this.liveChatButton?.addEventListener("click", this.#onChatClick);
    document.addEventListener("sierra-chat:close", this.#onChatCloseBound);
    this.#scheduleContentActivation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.refs.details.removeEventListener("toggle", this.#handleToggle);
    this.liveChatButton?.removeEventListener("click", this.#onChatClick);
    document.removeEventListener("sierra-chat:close", this.#onChatCloseBound);
    this.#removeIntentListeners();
  }

  /**
   * Re-insert deferred content if a re-render brought the templates back after activation
   */
  updatedCallback() {
    if (this.#contentActivated) this.#insertDeferredContent();
    super.updatedCallback();
  }

  /** @returns {HTMLElement | null} The menu button that opens the drawer */
  get #menuButton() {
    return this.refs.details.querySelector(":scope > summary");
  }

  /**
   * Build the deferred drawer content on the first sign of intent on the menu button.
   * Visitors who never reach for the menu never pay for its ~2,400 elements.
   */
  #scheduleContentActivation() {
    if (!this.querySelector("template[data-drawer-content]")) {
      this.#contentActivated = true;
      return;
    }

    INTENT_EVENTS.forEach((type) =>
      this.#menuButton?.addEventListener(type, this.#activateContent, {
        passive: true,
      }),
    );
  }

  #removeIntentListeners() {
    INTENT_EVENTS.forEach((type) =>
      this.#menuButton?.removeEventListener(type, this.#activateContent),
    );
  }

  /**
   * Swap the drawer's inert templates for their content and rebuild refs
   */
  #activateContent = () => {
    if (this.#contentActivated || !this.isConnected) return;

    this.#contentActivated = true;
    this.#removeIntentListeners();
    // Rebuild refs now rather than waiting on the base class's idle mutation observer
    this.updatedCallback();
  };

  #insertDeferredContent() {
    const templates = this.querySelectorAll("template[data-drawer-content]");
    if (!templates.length) return;

    templates.forEach((template) => {
      if (template instanceof HTMLTemplateElement) {
        template.replaceWith(template.content);
      }
    });
    this.#setupAnimatedElementListeners();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Open the first left nav item when the drawer is opened
   */
  #handleToggle = () => {
    if (!this.isOpen) return;

    this.leftNavDetails.forEach((leftNavItem, i) => {
      leftNavItem.open = i == 0;
    });
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * @returns {Array<HTMLDetailsElement>} list of left nav items
   */
  get leftNavDetails() {
    /** @type NodeListOf<HTMLDetailsElement> */
    const leftNavDetails = this.refs.details.querySelectorAll(
      "details.menu-drawer__left-nav-item",
    );

    return Array.from(leftNavDetails);
  }

  /**  @returns {Element | null} */
  get liveChatButton() {
    return this.querySelector(".button[data-sierra-chat]");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    this.#activateContent();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "true");

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add("menu-open");

      if (target) {
        this.refs.menuDrawer.classList.add("menu-drawer--has-submenu-opened");
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector(
        ".menu-drawer, .menu-drawer__submenu",
      );
      onAnimationEnd(drawer || details, () => trapFocus(details), {
        subtree: false,
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");
    this.refs.menuDrawer.classList.remove("menu-drawer--has-submenu-opened");

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll(
            "details[open]:not(accordion-custom > details)",
          );
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false },
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      "accordion-custom .details-content",
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add("details-content--no-animation");
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove("details-content--no-animation");
        }
      });
    }, 100);
  }

  /** Remove the trap focus within the drawer when the Sierra chat modal is opened */
  #onChatClick() {
    removeTrapFocus();
  }

  /** Re-trap the focus within the drawer when the Sierra chat modal closes */
  #onChatClose() {
    if (this.isOpen) {
      trapFocus(this.refs.details);
    }
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
